import React, { useState } from 'react';
import { FaPlus, FaMinus, FaRedo, FaUniversalAccess } from 'react-icons/fa';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1); // Default scale 1 (100%)

  // Apply proportional zoom - different approach for zoom in vs zoom out
  const applyZoom = (scale) => {
    const root = document.getElementById('root');
    let styleTag = document.getElementById('accessibility-zoom-styles');
    
    if (styleTag) {
      styleTag.remove();
    }
    
    if (!root) return;
    
    if (scale === 1) {
      // Reset to normal
      root.style.transform = 'none';
      document.documentElement.style.fontSize = '100%';
      document.body.style.minHeight = '100vh';
      return;
    }
    
    if (scale > 1) {
      // ZOOM IN: Use transform scale (works great, no issues)
      root.style.transform = `scale(${scale})`;
      root.style.transformOrigin = 'top center';
      root.style.transition = 'transform 0.3s ease';
      document.documentElement.style.fontSize = '100%';
      document.body.style.minHeight = `${100 * scale}vh`;
    } else {
      // ZOOM OUT: Use font-size only (no white space)
      root.style.transform = 'none';
      document.documentElement.style.fontSize = `${scale * 100}%`;
      document.body.style.minHeight = '100vh';
      
      // Add styles to ensure full width
      styleTag = document.createElement('style');
      styleTag.id = 'accessibility-zoom-styles';
      styleTag.innerHTML = `
        body, #root {
          width: 100vw !important;
          max-width: 100vw !important;
          overflow-x: hidden !important;
        }
        
        /* Keep widget unaffected */
        #accessibility-widget-container,
        #accessibility-widget-root,
        #accessibility-widget-root * {
          font-size: 16px !important;
          transform: none !important;
        }
      `;
      document.head.appendChild(styleTag);
    }
  };

  // Increase zoom
  const handleZoomIn = () => {
    const newZoom = Math.min(zoomLevel + 0.1, 1.6); // Max 160%
    setZoomLevel(newZoom);
    applyZoom(newZoom);
  };

  // Decrease zoom
  const handleZoomOut = () => {
    const newZoom = Math.max(zoomLevel - 0.1, 0.4); // Min 40%
    setZoomLevel(newZoom);
    applyZoom(newZoom);
  };

  // Reset zoom to default
  const handleReset = () => {
    setZoomLevel(1);
    applyZoom(1);
  };

  // Toggle menu
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const buttonStyle = {
    width: '48px',
    height: '48px',
    backgroundColor: '#ea5f45',
    border: 'none',
    transition: 'all 0.3s ease',
    fontSize: '16px'
  };

  const hoverStyle = {
    backgroundColor: '#d54d35',
    transform: 'scale(1.1)'
  };

  return (
    <div 
      id="accessibility-widget-root"
      className="position-fixed bottom-0 start-0 d-flex flex-column-reverse gap-3 mb-3 ms-3"
      style={{ zIndex: 99999 }}
    >
      {/* Accessibility Toggle Button - Always Visible */}
      <button
        onClick={toggleMenu}
        className="btn btn-danger rounded shadow d-flex align-items-center justify-content-center"
        style={buttonStyle}
        aria-label="Toggle Accessibility Menu"
        title="Accessibility Options"
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = hoverStyle.backgroundColor;
          e.currentTarget.style.transform = hoverStyle.transform;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = buttonStyle.backgroundColor;
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <FaUniversalAccess size={24} />
      </button>

      {/* Action Buttons - Show/Hide based on isOpen */}
      {isOpen && (
        <>
          <button
            onClick={handleReset}
            className="btn btn-danger rounded shadow d-flex align-items-center justify-content-center"
            style={{ ...buttonStyle, animation: 'fadeIn 0.3s ease-out forwards' }}
            aria-label="Reset Zoom"
            title="Reset to default size"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = hoverStyle.backgroundColor;
              e.currentTarget.style.transform = hoverStyle.transform;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = buttonStyle.backgroundColor;
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <FaRedo size={20} />
          </button>

          <button
            onClick={handleZoomOut}
            className="btn btn-danger rounded shadow d-flex align-items-center justify-content-center"
            style={{ ...buttonStyle, animation: 'fadeIn 0.3s ease-out forwards', animationDelay: '0.05s' }}
            aria-label="Zoom Out"
            title="Decrease size"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = hoverStyle.backgroundColor;
              e.currentTarget.style.transform = hoverStyle.transform;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = buttonStyle.backgroundColor;
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <FaMinus size={20} />
          </button>

          <button
            onClick={handleZoomIn}
            className="btn btn-danger rounded shadow d-flex align-items-center justify-content-center"
            style={{ ...buttonStyle, animation: 'fadeIn 0.3s ease-out forwards', animationDelay: '0.1s' }}
            aria-label="Zoom In"
            title="Increase size"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = hoverStyle.backgroundColor;
              e.currentTarget.style.transform = hoverStyle.transform;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = buttonStyle.backgroundColor;
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            <FaPlus size={20} />
          </button>
        </>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Prevent widget from being affected by page zoom */
        #accessibility-widget-root {
          transform: none !important;
          position: fixed !important;
        }
        
        /* Smooth zoom transition */
        #root {
          will-change: transform;
        }
      `}</style>
    </div>
  );
};

export default AccessibilityWidget;